import { useQuery } from '@tanstack/react-query';
import { useCallback, useEffect, useMemo, useReducer, useState, type PropsWithChildren } from 'react';
import { useLocation } from 'react-router-dom';
import { useStoreActions, useStoreState } from '~/store';
import { securityService } from '~/services';
import { arrayToMap, queryCreator } from './useQueryCreator';
import { resolvePartnerOptInsStatus } from './partnerOptInsStatus';
import { PARTNER_VIEW_HEADER_KEY, SessionContext } from './SessionContext';
import { isShipiumEmail } from '~/utils/regex';
import config, { AUTH_PATHS } from '../config';
import { useCookies } from 'react-cookie';

const { REFRESH_TOKEN_NAME, ACCESS_TOKEN_NAME, PARTNER_COOKIE_NAME, LOGGED_IN_COOKIE, COOKIE_TOKEN_EXPIRES } = config;

const partnerViewReducer = (state: boolean, action: { type: 'set' | 'unset' }) => {
  switch (action.type) {
    case 'set':
      localStorage.setItem(PARTNER_VIEW_HEADER_KEY, '1');
      return true;
    case 'unset':
      localStorage.removeItem(PARTNER_VIEW_HEADER_KEY);
      return false;
    default:
      return state;
  }
};

const COOKIE_KEYS = [
  ACCESS_TOKEN_NAME,
  REFRESH_TOKEN_NAME,
  PARTNER_COOKIE_NAME,
  LOGGED_IN_COOKIE,
  COOKIE_TOKEN_EXPIRES,
  'pids', // No longer used?
];

export const SessionProvider = ({ children }: PropsWithChildren<unknown>) => {
  const user = useStoreState((state) => state.session.user);
  const [partnerView, setPartnerView] = useReducer(partnerViewReducer, !!localStorage.getItem(PARTNER_VIEW_HEADER_KEY));

  /**
   * sessionPartnerId: "72045f0d-ed06-45ca-80f7-21d52e75472a"
   * sessionPartnerUrl: "just-great-cables"
   */
  const { sessionPartnerId = '', sessionPartnerUrl = '' } = user || {};
  const hasShipiumEmail = useMemo(() => isShipiumEmail(user?.email), [user?.email]);

  const partnerOptInsQuery = useQuery({
    ...queryCreator(sessionPartnerUrl, securityService.optIns.partnerList, {
      args: [sessionPartnerId],
    }),
    enabled: !!sessionPartnerId && !!sessionPartnerUrl,
    select: (data) => arrayToMap(data, 'optInId'),
  });

  const {
    data: partnerOptIns = {},
    isLoading: isPartnerOptInsLoading,
    refetch: refetchPartnerOptIns,
  } = partnerOptInsQuery;

  const partnerOptInsStatus = resolvePartnerOptInsStatus(partnerOptInsQuery);

  const isOptInEnabled = useCallback(
    (optInId: string) => {
      return partnerOptIns[optInId]?.active ?? false;
    },
    [partnerOptIns],
  );

  const location = useLocation();
  const isLoggedIn = useStoreState((state) => state.session.isLoggedIn);
  const refreshSessionSilent = useStoreActions((actions) => actions.session.refreshSessionSilent);
  const [cookies, , removeCookie] = useCookies(COOKIE_KEYS);
  const [returnToLogin, setReturnToLogin] = useState(!cookies[LOGGED_IN_COOKIE]);

  useEffect(() => {
    // Skip refresh on auth-related pages to avoid unnecessary 401s
    if (AUTH_PATHS.some((path) => location.pathname.startsWith(path))) {
      return;
    }

    const hasCookies = Object.keys(cookies).filter((key) => COOKIE_KEYS.includes(key)).length > 0;
    if (!isLoggedIn && hasCookies) {
      refreshSessionSilent().then((result: unknown) => {
        if (result) {
          return;
        }

        COOKIE_KEYS.forEach((key) => {
          removeCookie(key);
        });

        setReturnToLogin(true);
      });
    }
  }, [refreshSessionSilent, isLoggedIn, sessionPartnerUrl, cookies, removeCookie, location.pathname]);

  const sessionContextValue = useMemo(
    () => ({
      user,
      sessionPartnerUrl,
      sessionPartnerId,
      partnerOptIns,
      partnerOptInsStatus,
      refetchPartnerOptIns,
      isOptInEnabled,
      hasShipiumEmail,
      isShipiumView: hasShipiumEmail && !partnerView,
      setPartnerView,
      isPartnerOptInsLoading,
      returnToLogin: returnToLogin && !user,
    }),
    [
      user,
      sessionPartnerUrl,
      sessionPartnerId,
      partnerOptIns,
      partnerOptInsStatus,
      refetchPartnerOptIns,
      isOptInEnabled,
      hasShipiumEmail,
      partnerView,
      setPartnerView,
      isPartnerOptInsLoading,
      returnToLogin,
    ],
  );

  return <SessionContext.Provider value={sessionContextValue}>{children}</SessionContext.Provider>;
};
